"use client";

/**
 * Schedule Card Tile
 * One schedule item as a card in the Cards layout: tinted in its first label's color (white when it
 * has none, the brand tint while it is in progress), with the time and a "Now" pill on top, then the
 * title, details and label pills. Hover shows the builder-only delete button.
 */

import type React from "react";
import { useMemo } from "react";

import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { formatScheduleItemTime } from "../../../../_logic/utils/schedule-display-format";
import { getScheduleLabelTint } from "../../../../_logic/utils/schedule-label-colors";
import { formatStartsIn } from "../../../../_logic/utils/schedule-utils";
import { ItemDeleteButton } from "./ItemDeleteButton";
import { getFirstLabel } from "./label-utils";
import { ScheduleItemPills } from "./ScheduleItemPills";
import { ScheduleLabelPill } from "./ScheduleLabelPill";
import type { ScheduleRowConfig } from "./ScheduleRow";

interface ScheduleCardTileProps {
  /** Shared render context; `surface` is ignored (a tile is its own card). */
  config: ScheduleRowConfig;
  labelMap: Map<number, ScheduleLabelRow>;
  item: ScheduleItemRow;
  isLive?: boolean;
  startsInMinutes?: number;
}

/**
 * Renders one item card.
 *
 * @param props - See ScheduleCardTileProps.
 * @returns The card element.
 */
export function ScheduleCardTile({
  config,
  labelMap,
  item,
  isLive = false,
  startsInMinutes,
}: ScheduleCardTileProps): React.ReactElement {
  const { labels, primaryColor, onDelete, onItemClick, viewerRegistrations } = config;
  const firstLabelColor = getFirstLabel(item, labelMap)?.color ?? null;
  const tintColor = isLive ? primaryColor : firstLabelColor;
  const tint = useMemo(() => (tintColor ? getScheduleLabelTint(tintColor) : null), [tintColor]);
  const details = [item.location, item.speaker, item.vendor].filter((value): value is string => Boolean(value));

  return (
    <div
      className={`group/item flex h-full flex-col gap-1.5 rounded-2xl border p-4 sm:p-5 ${
        tint ? "" : "border-gray-200 bg-white"
      } ${onItemClick ? "cursor-pointer touch-manipulation transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400" : ""}`}
      style={tint ? { backgroundColor: isLive ? tint.background : tint.surface, borderColor: tint.border } : undefined}
      onClick={onItemClick ? () => onItemClick(item) : undefined}
      role={onItemClick ? "button" : undefined}
      tabIndex={onItemClick ? 0 : undefined}
      onKeyDown={
        onItemClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onItemClick(item);
              }
            }
          : undefined
      }
    >
      <div className="flex min-h-6 items-center justify-between gap-2">
        <span className="text-sm font-semibold tabular-nums text-gray-800">{formatScheduleItemTime(item)}</span>
        <span className="flex items-center gap-1">
          {isLive ? (
            <ScheduleLabelPill color={primaryColor}>Now</ScheduleLabelPill>
          ) : (
            startsInMinutes != null && (
              <ScheduleLabelPill color={primaryColor}>Starts in {formatStartsIn(startsInMinutes)}</ScheduleLabelPill>
            )
          )}
          {onDelete && <ItemDeleteButton item={item} onDelete={onDelete} />}
        </span>
      </div>
      <p className="wrap-break-word text-base font-semibold leading-snug text-gray-900">{item.title}</p>
      {details.map((detail) => (
        <p key={detail} className="wrap-break-word text-sm font-medium text-gray-600">
          {detail}
        </p>
      ))}
      <div className="mt-auto pt-2 empty:hidden">
        <ScheduleItemPills item={item} labels={labels} viewerRegistrations={viewerRegistrations} />
      </div>
    </div>
  );
}
